import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const InlineEditContext = createContext(null);

export const InlineEditProvider = ({ children }) => {
  const { user } = useAuth();
  const [editMode, setEditMode] = useState(false);
  const isAdmin = !!user;

  const toggleEditMode = useCallback(() => {
    setEditMode(prev => {
      if (!prev) toast.info('Inline edit mode enabled — click any highlighted text to edit');
      return !prev;
    });
  }, []);

  const saveField = useCallback(async (pageType, pageId, fieldPath, newValue) => {
    try {
      // Fetch current page content
      const { data } = await axios.get(`${API}/admin/pages/${pageType}/${pageId}`);
      const content = data.content || {};

      // Update the specific field (supports nested paths like 'hero.badge')
      const parts = fieldPath.split('.');
      if (parts.length === 1) {
        content[parts[0]] = newValue;
      } else {
        let obj = content;
        for (let i = 0; i < parts.length - 1; i++) {
          if (!obj[parts[i]]) obj[parts[i]] = {};
          obj = obj[parts[i]];
        }
        obj[parts[parts.length - 1]] = newValue;
      }

      // Save back
      await axios.put(`${API}/admin/pages/${pageType}/${pageId}`, {
        content,
        label: data.label,
      });
      toast.success('Content saved');
      return true;
    } catch (err) {
      toast.error('Failed to save — make sure this page is seeded in the CMS');
      return false;
    }
  }, []);

  return (
    <InlineEditContext.Provider value={{ editMode, toggleEditMode, saveField, isAdmin }}>
      {children}
    </InlineEditContext.Provider>
  );
};

export const useInlineEdit = () => {
  const ctx = useContext(InlineEditContext);
  if (!ctx) return { editMode: false, isAdmin: false };
  return ctx;
};
